import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Zap, Crown, Rocket, CreditCard, Clock, Download, AlertCircle, Loader2, CheckCircle2, Sparkles } from "lucide-react";

const PLAN_ICONS = { starter: Rocket, growth: Zap, enterprise: Crown };

function StatusPill({ status, days }) {
  const map = {
    trialing: { bg: "hsl(38 92% 90%)", fg: "hsl(38 92% 35%)", label: `Trial · ${days}d left` },
    active: { bg: "hsl(158 74% 92%)", fg: "hsl(158 74% 30%)", label: "Active" },
    past_due: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)", label: "Past due" },
    locked: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)", label: "Locked" },
    cancelled: { bg: "hsl(220 15% 90%)", fg: "hsl(220 30% 35%)", label: "Cancelled" },
    comped: { bg: "hsl(227 44% 90%)", fg: "hsl(227 44% 30%)", label: "Comped" },
  };
  const s = map[status] || map.trialing;
  return (
    <span className="text-[0.68rem] font-semibold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function PlanCard({ plan, currentPlanId, onSelect, loading, locked, status }) {
  const Icon = PLAN_ICONS[plan.id];
  const isCurrent = currentPlanId === plan.id && (status === "active" || status === "comped");
  return (
    <div
      className={`relative rounded-2xl p-6 border-2 flex flex-col transition-all ${
        isCurrent ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"
      }`}
      data-testid={`plan-${plan.id}`}
    >
      {isCurrent && <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[0.65rem] tracking-wider uppercase font-bold px-3 py-1 rounded-full">Current</div>}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCurrent ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display font-bold text-lg">{plan.name}</div>
          <div className="text-xs text-muted-foreground">{plan.tagline}</div>
        </div>
      </div>
      <div className="flex items-baseline gap-1 mb-5">
        <span className="font-display font-black text-4xl">{plan.price_display}</span>
        <span className="text-sm text-muted-foreground">/ month</span>
      </div>
      <ul className="space-y-2 flex-1 mb-5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSelect(plan)}
        disabled={loading || isCurrent}
        className="rounded-2xl h-11 font-semibold"
        variant={isCurrent ? "outline" : "default"}
        data-testid={`select-${plan.id}`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" />
         : isCurrent ? "Current plan"
         : locked ? "Unlock with this plan"
         : (status === "trialing" ? "Start subscription" : "Switch plan")}
      </Button>
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);

  const { data: status, isLoading: loadingStatus } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
    refetchInterval: 30000,
  });
  const { data: plans } = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => (await api.get("/billing/plans")).data,
  });
  const { data: payments } = useQuery({
    queryKey: ["billing-payments"],
    queryFn: async () => (await api.get("/billing/payments")).data,
  });

  if (loadingStatus) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const checkout = async (plan) => {
    setSelected(plan);
    setProcessing(true);
    try {
      const res = await api.post("/billing/checkout", {
        plan: plan.id,
        origin_url: window.location.origin,
      });
      if (res.data.demo_mode) {
        // Keep dialog open; user confirms in the dialog
        setSelected({ ...plan, tx_ref: res.data.tx_ref });
      } else {
        window.location.assign(res.data.checkout_url);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setProcessing(false); }
  };

  const confirmDemo = async () => {
    try {
      setProcessing(true);
      await api.post("/billing/demo-confirm", { tx_ref: selected.tx_ref });
      toast.success(`Subscribed to ${selected.name}!`);
      setSelected(null);
      qc.invalidateQueries({ queryKey: ["billing-status"] });
      qc.invalidateQueries({ queryKey: ["billing-payments"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setProcessing(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription? Your workspace will be locked at the end of this billing period.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription cancelled");
      qc.invalidateQueries({ queryKey: ["billing-status"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadInvoice = (p) => {
    const html = `<!doctype html><html><head><title>Sparkle Invoice ${p.tx_ref}</title>
      <style>body{font-family:system-ui;padding:40px;max-width:640px;margin:auto}h1{margin:0}
      .brand{color:#10b981;font-size:12px;letter-spacing:2px;text-transform:uppercase;font-weight:700}
      table{width:100%;margin-top:30px;border-collapse:collapse}
      td{padding:10px 0;border-bottom:1px solid #e5e7eb}
      .total{font-weight:700;font-size:18px}</style></head>
      <body><div class="brand">Sparkle</div><h1>Invoice</h1>
      <p>${new Date(p.paid_at).toLocaleString()}<br/>Ref: ${p.tx_ref}</p>
      <table><tr><td>Plan</td><td style="text-align:right">${(p.plan||"").toUpperCase()} subscription</td></tr>
      <tr><td class="total">Total</td><td class="total" style="text-align:right">${p.currency} ${Number(p.amount).toFixed(2)}</td></tr>
      <tr><td>Status</td><td style="text-align:right;color:#10b981;text-transform:capitalize">${p.status}</td></tr>
      <tr><td>Mode</td><td style="text-align:right;color:#6b7280;text-transform:capitalize">${p.mode}</td></tr></table>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`;
    const w = window.open("", "_blank", "width=640,height=800");
    if (!w) return toast.error("Popup blocked");
    w.document.write(html); w.document.close();
  };

  const locked = status.status === "locked";
  const trialing = status.status === "trialing";
  const planIcon = PLAN_ICONS[status.plan?.id] || Rocket;
  const CurrentPlanIcon = planIcon;

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-8">
        <div className="label-caps text-muted-foreground mb-2">Workspace</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Billing & Subscription</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Manage your Sparkle plan, payment method and invoices.
          {status.demo_mode && (
            <span className="ml-2 inline-flex items-center gap-1 text-primary font-semibold">
              <Sparkles className="w-3 h-3" /> DEMO mode — simulated payments
            </span>
          )}
        </p>
      </div>

      {/* Current status */}
      <div className={`rounded-2xl p-6 mb-6 border ${locked ? "border-destructive bg-destructive/5" : "border-border bg-card"}`} data-testid="billing-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${locked ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`}>
              {locked ? <AlertCircle className="w-6 h-6" /> : <CurrentPlanIcon className="w-6 h-6" />}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display font-bold text-xl">{status.plan.name} plan</div>
                <StatusPill status={status.status} days={status.trial_days_left} />
              </div>
              <div className="text-sm text-muted-foreground">
                {status.status === "comped"
                  ? "Billing is disabled on your workspace — enjoy Sparkle on us."
                  : locked
                    ? "Your trial has ended. Choose a plan below to continue."
                    : trialing
                      ? `${status.trial_days_left} day${status.trial_days_left !== 1 ? "s" : ""} remain in your free trial`
                      : status.card_last4
                        ? `Card ending ·${status.card_last4} · Next charge soon`
                        : "No payment method on file"}
              </div>
            </div>
          </div>
          {status.status === "active" && (
            <Button variant="outline" onClick={cancel} data-testid="cancel-sub" className="rounded-xl">Cancel subscription</Button>
          )}
        </div>

        {/* Usage */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          {[
            { k: "facilities", label: "Facilities", limit: status.plan.max_facilities },
            { k: "staff", label: "Staff", limit: status.plan.max_staff },
            { k: "templates", label: "Templates", limit: status.plan.max_templates },
          ].map(({ k, label, limit }) => {
            const used = status.usage[k];
            const pct = limit ? Math.min(100, (used / limit) * 100) : 15;
            const warn = limit && used >= limit;
            return (
              <div key={k} className="rounded-xl border border-border p-3 bg-muted/20">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground font-medium">{label}</span>
                  <span className={`font-mono font-semibold ${warn ? "text-destructive" : ""}`}>
                    {used}{limit ? ` / ${limit}` : ""}
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                  <div className={`h-full rounded-full ${warn ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Plans */}
      <div className="mb-6">
        <h2 className="font-display text-xl font-semibold mb-4">
          {locked ? "Choose a plan to unlock Sparkle" : trialing ? "Lock in a plan when you're ready" : "Change plan"}
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {(plans || []).map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              currentPlanId={status.plan?.id}
              onSelect={checkout}
              loading={processing && selected?.id === p.id && !selected.tx_ref}
              locked={locked}
              status={status.status}
            />
          ))}
        </div>
      </div>

      {/* Invoices */}
      <div className="border border-border bg-card rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <div className="label-caps text-muted-foreground">Invoices</div>
          <div className="font-display text-lg font-semibold">Payment history</div>
        </div>
        <div className="divide-y divide-border">
          {(payments || []).map((p) => (
            <div key={p.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`payment-${p.id}`}>
              <div className="min-w-0">
                <div className="font-medium capitalize">{p.plan} — {p.currency} {Number(p.amount).toFixed(2)}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {new Date(p.paid_at).toLocaleDateString()} · {p.tx_ref}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={p.status === "successful" ? "active" : p.status} />
                <Button size="sm" variant="ghost" onClick={() => downloadInvoice(p)}>
                  <Download className="w-3.5 h-3.5 mr-1" />Invoice
                </Button>
              </div>
            </div>
          ))}
          {(!payments || payments.length === 0) && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No payments yet.
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selected?.tx_ref} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm {selected?.name} subscription</DialogTitle>
            <DialogDescription>
              This workspace is in demo mode — click confirm to simulate a successful card payment and activate the plan immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 text-sm space-y-2">
            <div className="flex justify-between"><span>Plan</span><span className="font-semibold">{selected?.name}</span></div>
            <div className="flex justify-between"><span>Monthly charge</span><span className="font-semibold">{selected?.price_display}</span></div>
            <div className="flex justify-between">
              <span>{status.status === "active" ? "Takes effect" : "Trial"}</span>
              <span className="text-muted-foreground">
                {status.status === "active" ? "Replaces current plan immediately" : "Ends immediately"}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button onClick={confirmDemo} disabled={processing} data-testid="confirm-demo-payment">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" />Simulate successful payment</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
